import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";

const STATUS_STYLES = {
  draft:    "bg-gray-100 text-gray-700",
  sent:     "bg-brand-teal/15 text-brand-teal",
  accepted: "bg-brand-success/10 text-brand-success",
};

function money(n) {
  return (Number(n) || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" });
}

export default function PainterQuotesList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/painter/quotes")
      .then((r) => setRows(r.data || []))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-6 max-w-5xl" data-testid="painter-quotes-list">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold">Pro · Quote workflow</div>
          <h1 className="text-2xl font-bold text-brand-navy">Your quotes</h1>
        </div>
        <Link to="/painter/leads" className="text-sm text-brand-teal underline" data-testid="quotes-link-to-leads">
          Create a quote from a lead →
        </Link>
      </div>

      {err && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}
      {loading ? (
        <div className="text-brand-navy">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-brand-border rounded-lg p-8 text-center text-gray-600" data-testid="painter-quotes-empty">
          <p className="mb-2">No quotes yet.</p>
          <p className="text-sm">Open a lead in <Link to="/painter/leads" className="text-brand-teal underline">Leads</Link> and click <em>Create quote</em> to start.</p>
        </div>
      ) : (
        <div className="bg-white border border-brand-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-brand-cream text-brand-navy">
              <tr>
                <th className="text-left px-4 py-2">Quote #</th>
                <th className="text-left px-4 py-2">Homeowner</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Total</th>
                <th className="text-left px-4 py-2">Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((q) => (
                <tr key={q.id} className="border-t border-brand-border hover:bg-brand-cream/40" data-testid={`quote-row-${q.id}`}>
                  <td className="px-4 py-2 font-semibold text-brand-navy">{q.quote_number}</td>
                  <td className="px-4 py-2">{q.homeowner?.name || "—"}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold uppercase ${STATUS_STYLES[q.status] || "bg-gray-100 text-gray-700"}`}>
                      {q.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">{money(q.total)}</td>
                  <td className="px-4 py-2 text-gray-600">{new Date(q.created_at).toLocaleDateString("en-AU")}</td>
                  <td className="px-4 py-2 text-right">
                    <Link className="text-brand-teal underline text-sm" to={`/painter/quotes/${q.id}`} data-testid={`quote-open-${q.id}`}>Open</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
